import { readRuntimeSettings } from './environment.runtime';
import { RuntimeOrganization, RuntimeRoleFlags, RuntimeSelection, RuntimeUser } from './environment.types';

// -----------------------------------------------------------------------------------------------------
// @ Environment shape — the single public interface consumed throughout the app.
//
//   Static config (url, app, support, layout) comes from window globals injected by the server.
//   Runtime state (user, roles, access, organizations, selection) is populated after login and
//   mutated in-place as the user navigates. Both groups are persisted to localStorage and
//   re-hydrated on the next page load.
// -----------------------------------------------------------------------------------------------------

export type EnvironmentShape = {
    production: boolean;

    // ---- Static config (set at deploy / server-render time) ----------------------------------------
    url: {
        api: string;      // Base URL for all API calls, e.g. "https://api.ashcorp.xyz"
        web: string;      // Web front-end URL
        mobile: string;   // Mobile app URL
    };
    app: {
        name: string;     // Display name shown in the UI
        code: string;     // Internal tenant/app code
    };
    support: {
        email: string;
        mobile: string;
        fullname: string;
    };
    layout: {
        logo: string;
        theme: string;
        watermark: string;
        favicon: string;
    };

    // ---- Runtime state (mutated after login / org selection) ----------------------------------------
    user: RuntimeUser;
    roles: RuntimeRoleFlags;
    access: Record<string, any>;
    organizations: RuntimeOrganization[];
    selection: RuntimeSelection & {
        organizationData: any;   // Full organization object for the active workspace
        branchData: any;         // Full branch object for the active workspace
    };
};

const FALLBACKS = {
    url: {
        api: 'https://api.ashcorp.xyz',
        web: 'https://demo.ashcorp.xyz',
        mobile: 'https://mobile.ashcorp.co'
    },
    app: {
        name: 'DEMO',
        code: 'DEMO'
    },
    support: {
        email: 'support@ashcorp.co',
        mobile: '59729663',
        fullname: 'Addams'
    },
    theme: {
        logo: 'https://cloud.ashcorp.co/index.php/s/fX7kZWEkEysJrqb/download/logo-20260328151452-e9f33243.png',
        theme: 'default',
        watermark: 'https://cloud.ashcorp.co/index.php/s/Btp2mpragCTSniC/download/watermark-20260328151320-0bcf126c.png',
        favicon: 'https://cloud.ashcorp.co/index.php/s/MwMofkqidT9pYPZ/download/favicon-20260328151518-4af7f78e.png'
    },
    user: {
        avatar: 'https://cloud.ashcorp.co/index.php/s/g7ewbkDSAY6eTC3/download/avatar-20260328152021-746da634.jpeg'
    }
} as const;

function firstNonEmptyString(...values: Array<unknown>): string | null {
    for (const value of values) {
        if (typeof value !== 'string') {
            continue;
        }

        const trimmed = value.trim();
        if (trimmed.length > 0) {
            return trimmed;
        }
    }

    return null;
}

function normalizeApiUrl(value: string): string {
    return value.replace(/\/+$/, '');
}

function toFiniteNumber(value: unknown, fallback: number): number {
    return Number.isFinite(value as number) ? Number(value) : fallback;
}

export function createEnvironment(production: boolean): EnvironmentShape {
    const settings = readRuntimeSettings();

    // ---- Static config -------------------------------------------------------------------------
    const url = {
        api: normalizeApiUrl(firstNonEmptyString(
            settings.settings?.url?.api,
            settings.url?.api,
            FALLBACKS.url.api
        ) as string),
        web: firstNonEmptyString(
            settings.settings?.url?.web,
            settings.url?.web,
            FALLBACKS.url.web
        ) as string,
        mobile: firstNonEmptyString(
            settings.settings?.url?.mobile,
            settings.url?.mobile,
            FALLBACKS.url.mobile
        ) as string
    };

    const app = {
        name: firstNonEmptyString(
            settings.settings?.app?.name,
            settings.app?.name,
            FALLBACKS.app.name
        ) as string,
        code: firstNonEmptyString(
            settings.settings?.app?.code,
            settings.app?.code,
            FALLBACKS.app.code
        ) as string
    };

    const support = {
        email: firstNonEmptyString(
            settings.settings?.support?.email,
            settings.support?.email,
            FALLBACKS.support.email
        ) as string,
        mobile: firstNonEmptyString(
            settings.settings?.support?.mobile,
            settings.support?.mobile,
            FALLBACKS.support.mobile
        ) as string,
        fullname: firstNonEmptyString(
            settings.settings?.support?.fullname,
            settings.support?.fullname,
            FALLBACKS.support.fullname
        ) as string
    };

    const layout = {
        logo:      firstNonEmptyString(settings.theme?.logo,      settings.layout?.logo,      FALLBACKS.theme.logo)      as string,
        theme:     firstNonEmptyString(settings.theme?.theme,     settings.layout?.theme,     FALLBACKS.theme.theme)     as string,
        watermark: firstNonEmptyString(settings.theme?.watermark, settings.layout?.watermark, FALLBACKS.theme.watermark) as string,
        favicon:   firstNonEmptyString(settings.theme?.favicon,   settings.layout?.favicon,   FALLBACKS.theme.favicon)   as string
    };

    // ---- Runtime state -------------------------------------------------------------------------
    const user: RuntimeUser = {
        email:      firstNonEmptyString(settings.userState?.profile?.email,      settings.user?.email)      || '',
        fullname:   firstNonEmptyString(settings.userState?.profile?.fullname,   settings.user?.fullname)   || '',
        lastname:   firstNonEmptyString(settings.userState?.profile?.lastname,   settings.user?.lastname)   || '',
        othernames: firstNonEmptyString(settings.userState?.profile?.othernames, settings.user?.othernames) || '',
        roles:      settings.userState?.profile?.roles || settings.user?.roles || [],
        superadmin: settings.userState?.profile?.superadmin === true || settings.user?.superadmin === true,
        avatar:     firstNonEmptyString(
            settings.userState?.profile?.avatar,
            settings.user?.avatar,
            settings.app?.avatar,
            FALLBACKS.user.avatar
        ) as string
    };

    const roles = {
        super:   settings.userState?.roles?.super   === true || settings.roles?.super   === true,
        admin:   settings.userState?.roles?.admin   === true || settings.roles?.admin   === true,
        manager: settings.userState?.roles?.manager === true || settings.roles?.manager === true,
        user:    settings.userState?.roles?.user    === true || settings.roles?.user    === true
    };

    const organizations = settings.userState?.organizations || settings.organizations || [];

    const access: Record<string, any> = settings.userState?.access || settings.access || {};

    const selection = {
        organization:     toFiniteNumber(settings.selectionState?.indexes?.organization, toFiniteNumber(settings.selection?.organization, -1)),
        branch:           toFiniteNumber(settings.selectionState?.indexes?.branch,       toFiniteNumber(settings.selection?.branch,       -1)),
        organizationData: settings.selectionState?.organization || (settings as any).organization || null,
        branchData:       settings.selectionState?.branch       || (settings as any).branch       || null
    };

    return { production, url, app, support, layout, user, roles, access, organizations, selection };
}